import { env } from '../../env';
import {
    CockpitCustomer,
    CustomerIdAndRoleAndLoginSettings,
    LoginItem,
    RequestOptions,
    RoleItem,
    StageGroup,
    StageGroupingActive,
    StageHeader,
    ToastTypeEnum,
} from '../../models/Index';
import HttpService from '../httpService/HttpService';
import StateService from '../stateService/StateService';

const httpClient = HttpService.getAxiosClient();

const UD_BACKEND_URL = `${env.REACT_APP_BACKEND_URL}/v1`;

type InitialStageGroup = {
    name: string;
};

const changeLoginRole = async (payload: string[], login: string, options?: RequestOptions): Promise<string[]> => {
    return httpClient
        .post(`${UD_BACKEND_URL}/logins/${login}/roles`, payload, { ...options })
        .then((response) => {
            const defaultText = 'TOAST_MESSAGE.LOGIN_SETTINGS_SUCCESSFULLY_SAVED_TEXT';
            const msg = defaultText;
            StateService.toastMessage$.next({
                source: 'TOAST_MESSAGE.LOGIN_SETTINGS_SUCCESSFULLY_SAVED_TEXT',
                text: msg,
                type: ToastTypeEnum.SUCCESS,
            });
            return response.data;
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_CUSTOMER_ID_AND_ROLE_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'CUSTOMER_SERVICE.GET_CUSTOMER_ID_AND_ROLE', text: msg, type: ToastTypeEnum.ERROR });
        });
};

function getCustomerIdAndRoleAndLoginSettings(username: string, options?: RequestOptions): Promise<CustomerIdAndRoleAndLoginSettings> {
    return httpClient
        .get(`${UD_BACKEND_URL}/logins/${username}`, { ...options })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_CUSTOMER_ID_AND_ROLE_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'CUSTOMER_SERVICE.GET_CUSTOMER_ID_AND_ROLE', text: msg, type: ToastTypeEnum.ERROR });
        });
}

function getCustomerList(options?: RequestOptions): Promise<CockpitCustomer[]> {
    return httpClient
        .get(`${UD_BACKEND_URL}/customers`, { ...options })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            // const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_CUSTOMER_LIST_TEXT';
            // const msg = err.response?.data ? err.response.data : defaultText;
            // StateService.toastMessage$.next({ source: 'CUSTOMER_SERVICE.GET_CUSTOMER_LIST', text: msg, type: ToastTypeEnum.ERROR });
        });
}

function getCustomerLogins(customerUuid: string, options?: RequestOptions): Promise<LoginItem[]> {
    return httpClient
        .get(`${UD_BACKEND_URL}/customers/${customerUuid}/logins`, { ...options })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_LOGINS_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'CUSTOMER_SERVICE.GET_CUSTOMER_LOGINS', text: msg, type: ToastTypeEnum.ERROR });
        });
}

const getLoginRoles = (options?: RequestOptions): Promise<RoleItem[]> => {
    return httpClient
        .get(`${UD_BACKEND_URL}/roles`, { ...options })
        .then((response) => {
            return response?.data;
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_LOGIN_ROLES_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'CUSTOMER_SERVICE.GET_CUSTOMER_LOGINS', text: msg, type: ToastTypeEnum.ERROR });
        });
};

function getLoginStageGroupsAssignments(username: string, customerUuid: string, options?: RequestOptions): Promise<StageGroup[]> {
    return httpClient
        .get(`${UD_BACKEND_URL}/logins/${username}/stage-groups`, { ...options, headers: { customerUuid } })
        .then((response) => response.data)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_STAGE_GROUP_ASSIGNMENTS_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.GET_LOGIN_STAGE_GROUP_ASSIGNMENTS',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function createLoginStageGroupsAssignment(username: string, customerUuid: string, body: string[], options?: RequestOptions): Promise<any> {
    return httpClient
        .post(`${UD_BACKEND_URL}/logins/${username}/stage-groups`, body, { ...options, headers: { customerUuid: `${customerUuid}` } })
        .then((response) => response)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_CREATING_STAGE_GROUP_ASSIGNMENT_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.POST_LOGIN_STAGE_GROUP_ASSIGNMENTS',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function getStageGroupsForCustomer(customerUuid: string, options?: RequestOptions): Promise<StageGroup[]> {
    return httpClient
        .get(`${UD_BACKEND_URL}/stage-groups`, { ...options, headers: { customerUuid } })
        .then((response) => response.data)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_STAGE_GROUPS_FOR_CUSTOMER_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.GET_STAGE_GROUPS_FOR_CUSTOMER',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function createStageGroupForCustomer(customerUuid: string, body: InitialStageGroup, options?: RequestOptions): Promise<StageGroup> {
    return httpClient
        .post(`${UD_BACKEND_URL}/stage-groups`, body, { ...options, headers: { customerUuid } })
        .then((response) => response.data)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_CREATING_STAGE_GROUP_FOR_CUSTOMER_TEXT';
            let msg = err.response?.data ?? defaultText;
            if (err.response?.status === 409) {
                msg = 'TOAST_MESSAGE.ERROR_STAGE_GROUP_NAME_DUPLICATE_TEXT';
            }
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.POST_STAGE_GROUP_FOR_CUSTOMER',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function updateStageGroupForCustomer(customerUuid: string, stageGroup: StageGroup, options?: RequestOptions): Promise<any> {
    return httpClient
        .put(`${UD_BACKEND_URL}/stage-groups/${stageGroup.uuid}`, stageGroup, {
            ...options,
            headers: { customerUuid: `${customerUuid}` },
        })
        .then((response) => response)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_UPDATING_STAGE_GROUP_FOR_CUSTOMER_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.PUT_STAGE_GROUP_FOR_CUSTOMER',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function deleteStageGroupForCustomer(stageGroupUuid: string, customerUuid: string, options?: RequestOptions): Promise<any> {
    return httpClient
        .delete(`${UD_BACKEND_URL}/stage-groups/${stageGroupUuid}`, { ...options, headers: { customerUuid } })
        .then((response) => response)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_DELETING_STAGE_GROUP_FOR_CUSTOMER_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.DELETE_STAGE_GROUP_FOR_CUSTOMER',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function getStageGroupEntries(stageGroupUuid: string, customerUuid: string, options?: RequestOptions): Promise<StageHeader[]> {
    return httpClient
        .get(`${UD_BACKEND_URL}/stage-groups/${stageGroupUuid}/stage-headers`, { ...options, headers: { customerUuid } })
        .then((response) => response.data)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_STAGE_GROUP_ENTRIES_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.GET_STAGE_GROUP_ENTRIES',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function createStageGroupEntries(stageGroupUuid: string, customerUuid: string, body: string[], options?: RequestOptions): Promise<any> {
    return httpClient
        .post(`${UD_BACKEND_URL}/stage-groups/${stageGroupUuid}/stage-headers`, body, { ...options, headers: { customerUuid } })
        .then((response) => response)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_CREATING_STAGE_GROUP_ENTRIES_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.POST_STAGE_GROUP_ENTRIES',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function getIsStageGroupingActive(customerUuid: string, options?: RequestOptions): Promise<StageGroupingActive> {
    return httpClient
        .get(`${UD_BACKEND_URL}/stage-grouping-active`, { ...options, headers: { customerUuid } })
        .then((response) => response.data)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_STAGE_GROUPING_ACTIVE_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.GET_STAGE_GROUPING_ACTIVE',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function postIsStageGroupingActive(
    body: StageGroupingActive,
    customerUuid: string,
    options?: RequestOptions,
): Promise<StageGroupingActive> {
    return httpClient
        .post(`${UD_BACKEND_URL}/stage-grouping-active`, body, { ...options, headers: { customerUuid } })
        .then((response) => response.data)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_POSTING_STAGE_GROUPING_ACTIVE_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.POST_STAGE_GROUPING_ACTIVE',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function sortCockpitCustomersAlphabetically(cockpitCustomers: CockpitCustomer[]) {
    const result = cockpitCustomers.sort(function (a, b) {
        var nameA = a.tenantName?.toUpperCase() || '';
        var nameB = b.tenantName?.toUpperCase() || '';
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    });
    return result;
}

const CustomerService = {
    changeLoginRole,
    createStageGroupEntries,
    createLoginStageGroupsAssignment,
    createStageGroupForCustomer,
    deleteStageGroupForCustomer,
    getCustomerIdAndRoleAndLoginSettings,
    getCustomerList,
    getCustomerLogins,
    getIsStageGroupingActive,
    getStageGroupEntries,
    getStageGroupsForCustomer,
    getLoginRoles,
    getLoginStageGroupsAssignments,
    httpClient,
    postIsStageGroupingActive,
    sortCockpitCustomersAlphabetically,
    updateStageGroupForCustomer,
};

export default CustomerService;
